<template>
    <div>
        <!-- Render each modal in the stack -->
        <template v-for="(modal, index) in modalState.modals" :key="modal.id">
            <!-- Standalone component modal (rendered outside modal wrapper) -->
            <component 
                v-if="modal.standaloneComponent" 
                :is="modal.componentToRender" 
                v-bind="modal.componentProps" 
                v-on="getEventListeners(modal)"
                :style="{ zIndex: getZIndex(index) + 10 }"
            />
            
            <!-- Regular modal with custom implementation -->
            <Teleport to="body" v-else>
                <!-- Backdrop -->
                <Transition name="fade">
                    <div 
                        v-if="modal.isVisible"
                        class="modal-backdrop"
                        :style="{ zIndex: getZIndex(index) }"
                        @click="closeModal(modal.id, modal)"
                    />
                </Transition>
                
                <!-- Modal -->
                <Transition name="fade">
                    <div 
                        v-if="modal.isVisible"
                        class="modal"
                        :style="{ zIndex: getZIndex(index) + 10 }"
                        @click.self="closeModal(modal.id, modal)"
                    >
                        <div 
                            class="modal-dialog"
                            :class="getModalSizeClass(modal.size)"
                            @click.stop
                        >
                            <div class="modal-content">
                                <!-- Header -->
                                <div class="modal-header" v-if="modal.title">
                                    <h5 class="modal-title">{{ modal.title }}</h5>
                                    <button 
                                        type="button" 
                                        class="btn-close" 
                                        @click="closeModal(modal.id, modal)"
                                        aria-label="Close"
                                    />
                                </div>
                                
                                <!-- Body -->
                                <div class="modal-body">
                                    <component 
                                        :is="modal.componentToRender" 
                                        v-bind="modal.componentProps" 
                                        v-on="getEventListeners(modal)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </Teleport>
        </template>
    </div>
</template>
  
<script>
import { inject, onMounted, onBeforeUnmount, watch } from 'vue';

export default {
    name: 'Modal',
    setup() {
        const { modalState, hideModal, getZIndex } = inject('modal');
        
        function closeModal(modalId, modal) {
            // Check if the component has a custom hide handler
            if (modal.componentProps && modal.componentProps.hide) {
                modal.componentProps.hide();
                return;
            }
            
            // Otherwise close the modal
            hideModal(modalId);
        }

        // Generate event listeners for a specific modal
        function getEventListeners(modal) {
            const handlers = {};
            for (const [event, handler] of Object.entries(modal.eventHandlers || {})) {
                handlers[event] = handler;
            }
            return handlers;
        }

        // Get modal size class
        function getModalSizeClass(size) {
            if (size === 'sm') return 'modal-sm';
            if (size === 'lg') return 'modal-lg';
            if (size === 'xl') return 'modal-xl';
            return '';
        }

        // Handle ESC key to close topmost modal
        function handleEscape(e) {
            if (e.key === 'Escape' && modalState.modals.length > 0) {
                const topModal = modalState.modals[modalState.modals.length - 1];
                closeModal(topModal.id, topModal);
            }
        }

        // Lock body scroll when modals are open
        watch(
            () => modalState.modals.length,
            (count) => {
                if (count > 0) {
                    document.body.style.overflow = 'hidden';
                } else {
                    document.body.style.overflow = '';
                }
            }
        );

        onMounted(() => {
            document.addEventListener('keydown', handleEscape);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        });

        return {
            modalState,
            closeModal,
            getEventListeners,
            getZIndex,
            getModalSizeClass,
        };
    },
};
</script>
  
<style scoped>
/* Fade transition for modal and backdrop */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Modal backdrop */
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
}

/* Modal container */
.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    outline: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
}

/* Modal dialog */
.modal-dialog {
    position: relative;
    width: auto;
    max-width: 500px;
    margin: 0 auto;
}

.modal-dialog.modal-sm {
    max-width: 300px;
}

.modal-dialog.modal-lg {
    max-width: 800px;
}

.modal-dialog.modal-xl {
    max-width: 1140px;
}

/* Modal content */
.modal-content {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    pointer-events: auto;
    background-color: #272c33;
    background-clip: padding-box;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 0.3rem;
    outline: 0;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Modal header */
.modal-header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid #3f3f3f;
    border-top-left-radius: calc(0.3rem - 1px);
    border-top-right-radius: calc(0.3rem - 1px);
}

.modal-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.5;
}

/* Close button */
.btn-close {
    padding: 0.5rem;
    margin: -0.5rem -0.5rem -0.5rem auto;
    background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat;
    border: 0;
    border-radius: 0.25rem;
    opacity: 0.5;
    width: 1em;
    height: 1em;
    cursor: pointer;
}

.btn-close:hover {
    opacity: 0.75;
}

/* Modal body */
.modal-body {
    position: relative;
    flex: 1 1 auto;
    padding: 1rem;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
}

/* Responsive adjustments */
@media (min-width: 576px) {
    .modal-dialog {
        max-width: 500px;
    }
    
    .modal-dialog.modal-sm {
        max-width: 300px;
    }
    
    .modal-dialog.modal-lg {
        max-width: 1140px;
    }
    
    .modal-dialog.modal-xl {
        max-width: 100%;
    }
}
</style>
  